import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  FileText, Receipt, Pen, Camera, RefreshCw,
  Download, Send, ExternalLink, Square, CheckSquare,
  X, ZoomIn, Calendar, Search, Filter
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN   = () => sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
const H       = () => ({ Authorization: `Bearer ${TOKEN()}` });

const TYPE_CONFIG = {
  contrat:   { label: 'Contrat',   icon: <FileText size={13}/>,  color: 'var(--admin-teal)',   bg: 'var(--admin-teal-dim)' },
  facture:   { label: 'Facture',   icon: <Receipt size={13}/>,  color: 'var(--admin-gold)',   bg: 'var(--admin-gold-dim)' },
  signature: { label: 'Signature', icon: <Pen size={13}/>,      color: 'var(--admin-green)',  bg: 'var(--admin-green-dim)' },
  photo:     { label: 'Photo',     icon: <Camera size={13}/>,   color: 'var(--admin-blue)',   bg: 'var(--admin-blue-dim)' },
};

const TYPE_FILTERS = [
  { key: '',          label: 'Tous' },
  { key: 'contrat',   label: 'Contrats' },
  { key: 'facture',   label: 'Factures' },
  { key: 'signature', label: 'Signatures' },
  { key: 'photo',     label: 'Photos' },
];

// Composant aperçu inline
function InlinePreview({ doc, onClose }) {
  const type = doc.type;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 60, display: 'flex', flexDirection: 'column',
      background: 'rgba(4,6,9,.9)', backdropFilter: 'blur(6px)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 18px', background: 'var(--admin-bg2)',
        borderBottom: '1px solid var(--admin-border-soft)', flexShrink: 0,
      }}>
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--admin-text)' }}>
            {TYPE_CONFIG[type]?.label} — {doc.client_nom}
          </p>
          <p style={{ fontSize: 11.5, color: 'var(--admin-muted)' }}>{doc.numero} · {formatDate(doc.date)}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {doc.url_dl && (
            <a href={`${BACKEND}${doc.url_dl}`} target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 9,
                background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)', fontSize: 12.5, fontWeight: 700,
                textDecoration: 'none' }}>
              <Download size={13}/> Télécharger
            </a>
          )}
          <button onClick={onClose}
            style={{ padding: '7px 12px', borderRadius: 9, background: 'var(--admin-surface)',
              border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>
            <X size={15}/>
          </button>
        </div>
      </div>

      {/* Contenu aperçu */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {(type === 'contrat' || type === 'facture') && doc.url_view && (
          <iframe
            src={`${BACKEND}${doc.url_view}`}
            style={{ width: '100%', height: '100%', minHeight: 600, border: 'none',
              borderRadius: 12, background: '#fff' }}
            title={`${TYPE_CONFIG[type]?.label} — ${doc.numero}`}
          />
        )}
        {type === 'signature' && doc.signature_data && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <p style={{ fontSize: 12, color: 'var(--admin-muted)', marginBottom: 16 }}>
              Signature de {doc.client_nom} · {doc.statut}
            </p>
            <img src={doc.signature_data} alt="Signature client"
              style={{ maxWidth: 400, border: '1px solid var(--admin-border)', borderRadius: 12,
                background: '#fff', padding: 16 }} />
          </div>
        )}
        {type === 'photo' && doc.url_view && (
          <div style={{ textAlign: 'center' }}>
            <img src={doc.url_view} alt="Photo terrain"
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain' }} />
          </div>
        )}
        {!doc.url_view && !doc.signature_data && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-faint)' }}>
            Aperçu non disponible
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDocuments() {
  const [docs,      setDocs]      = useState([]);
  const [summary,   setSummary]   = useState({});
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  // Filtres
  const [typeFilter, setTypeFilter] = useState('');
  const [search,     setSearch]     = useState('');
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // Sélection multiple
  const [selected,  setSelected]  = useState(new Set());
  // Aperçu
  const [preview,   setPreview]   = useState(null);
  // Envoi
  const [sending,   setSending]   = useState({});
  const [zipping,   setZipping]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND}/api/admin/documents?limit=80`;
      if (typeFilter) url += `&type_filter=${typeFilter}`;
      if (search)     url += `&search=${encodeURIComponent(search)}`;
      if (dateFrom)   url += `&date_from=${dateFrom}`;
      if (dateTo)     url += `&date_to=${dateTo}`;
      const d = await fetch(url, { headers: H() }).then(r => r.json());
      setDocs(d.documents || []);
      setSummary(d.summary || {});
      setTotal(d.total || 0);
      setSelected(new Set()); // reset selection
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [typeFilter, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  async function renvoyer(doc) {
    if (!doc.can_resend) return;
    setSending(p => ({ ...p, [doc.id]: true }));
    try {
      const res = await fetch(
        `${BACKEND}/api/admin/documents/${doc.quote_id}/renvoyer/${doc.type}`,
        { method: 'POST', headers: H() }
      );
      const d = await res.json();
      if (d.success) toast.success(`${TYPE_CONFIG[doc.type]?.label} renvoyé !`);
      else toast.error(d.message || 'Erreur');
    } catch { toast.error('Erreur'); }
    setSending(p => ({ ...p, [doc.id]: false }));
  }

  async function downloadZip() {
    if (selected.size === 0) return;
    setZipping(true);
    try {
      const doc_ids = [...selected];
      const res = await fetch(`${BACKEND}/api/admin/documents/download-zip`, {
        method: 'POST',
        headers: { ...H(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_ids }),
      });
      if (!res.ok) { toast.error('Aucun document téléchargeable'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `documents-${new Date().toISOString().slice(0,10)}.zip`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${selected.size} document(s) téléchargé(s) en ZIP`);
    } catch { toast.error('Erreur téléchargement'); }
    setZipping(false);
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function selectAll() {
    selected.size === docs.length ? setSelected(new Set()) : setSelected(new Set(docs.map(d => d.id)));
  }

  return (
    <div data-testid="admin-documents">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Documents</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
              letterSpacing: '-.6px', color: 'var(--admin-text)' }}>Documents</h2>
            <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
              {total} document{total > 1 ? 's' : ''} — Contrats, factures, signatures et photos
            </p>
          </div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {selected.size > 0 && (
              <button onClick={downloadZip} disabled={zipping}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                  background: 'var(--admin-gold)', color: '#2a1d05', border: 'none',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: zipping ? .6 : 1 }}>
                <Download size={13}/> {zipping ? '...' : `ZIP (${selected.size})`}
              </button>
            )}
            <button onClick={() => setShowFilters(!showFilters)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 10,
                border: `1px solid ${showFilters ? 'var(--admin-teal-line)' : 'var(--admin-border)'}`,
                background: showFilters ? 'var(--admin-teal-dim)' : 'var(--admin-surface)',
                color: showFilters ? 'var(--admin-teal)' : 'var(--admin-muted)',
                fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              <Filter size={13}/> Filtres {(dateFrom || dateTo) ? '●' : ''}
            </button>
            <button onClick={load}
              style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid var(--admin-border)',
                background: 'var(--admin-surface)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-muted)' }}>
              <RefreshCw size={14}/>
            </button>
          </div>
        </div>
      </div>

      {/* KPI cards cliquables */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 14 }}>
        {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
          <div key={key} className="adm-card"
            onClick={() => setTypeFilter(typeFilter === key ? '' : key)}
            style={{ padding: '11px 13px', cursor: 'pointer', transition: 'border-color .15s',
              borderColor: typeFilter === key ? cfg.color : 'var(--admin-border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <span style={{ color: cfg.color }}>{cfg.icon}</span>
              <p style={{ fontSize: 11.5, color: 'var(--admin-muted)' }}>{cfg.label}</p>
            </div>
            <p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 22, fontWeight: 700, color: cfg.color }}>
              {summary[`${key}s`] || 0}
            </p>
          </div>
        ))}
      </div>

      {/* Filtres avancés */}
      {showFilters && (
        <div className="adm-card" style={{ padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--admin-muted)', marginBottom: 5 }}>Depuis</p>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ width: '100%', padding: '8px 11px', borderRadius: 9, boxSizing: 'border-box',
                  background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)',
                  color: 'var(--admin-text)', fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--admin-muted)', marginBottom: 5 }}>Jusqu'au</p>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ width: '100%', padding: '8px 11px', borderRadius: 9, boxSizing: 'border-box',
                  background: 'var(--admin-surface2)', border: '1px solid var(--admin-border)',
                  color: 'var(--admin-text)', fontSize: 13, outline: 'none' }} />
            </div>
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }}
              style={{ marginTop: 8, fontSize: 12, color: 'var(--admin-red)', background: 'none',
                border: 'none', cursor: 'pointer', padding: 0 }}>
              ✕ Effacer les dates
            </button>
          )}
        </div>
      )}

      {/* Chips type + recherche + select all */}
      <div style={{ display: 'flex', gap: 5, overflowX: 'auto', marginBottom: 10,
        paddingBottom: 4, scrollbarWidth: 'none' }}>
        {TYPE_FILTERS.map(f => (
          <button key={f.key} onClick={() => setTypeFilter(f.key)}
            style={{ flexShrink: 0, fontSize: 12.5, fontWeight: 600, padding: '7px 13px', borderRadius: 9,
              border: `1px solid ${typeFilter === f.key ? 'var(--admin-teal-line)' : 'var(--admin-border)'}`,
              background: typeFilter === f.key ? 'var(--admin-teal-dim)' : 'var(--admin-surface)',
              color: typeFilter === f.key ? 'var(--admin-teal)' : 'var(--admin-muted)',
              cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {f.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={13} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-faint)' }}/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher client, n° devis..."
            style={{ width: '100%', padding: '9px 14px 9px 32px', borderRadius: 10, boxSizing: 'border-box',
              background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)',
              color: 'var(--admin-text)', fontSize: 13.5, outline: 'none' }} />
        </div>
        {docs.length > 0 && (
          <button onClick={selectAll}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 9,
              background: 'var(--admin-surface)', border: '1px solid var(--admin-border)',
              color: 'var(--admin-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {selected.size === docs.length
              ? <><CheckSquare size={13}/> Tout désélectionner</>
              : <><Square size={13}/> Tout sélectionner</>}
          </button>
        )}
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1,2,3,4].map(i => <div key={i} className="adm-card" style={{ height: 68, background: 'var(--admin-surface2)' }} />)}
        </div>
      ) : docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--admin-faint)' }}>
          <FileText size={36} style={{ margin: '0 auto 12px', opacity: .2 }}/>
          <p style={{ fontSize: 14 }}>Aucun document trouvé</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {docs.map(doc => {
            const cfg   = TYPE_CONFIG[doc.type] || TYPE_CONFIG.contrat;
            const isSel = selected.has(doc.id);
            return (
              <div key={doc.id} className="adm-card"
                style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
                  borderColor: isSel ? 'var(--admin-teal-line)' : 'var(--admin-border-soft)',
                  background: isSel ? 'var(--admin-teal-dim)' : 'var(--admin-surface)' }}>
                {/* Checkbox */}
                <button onClick={() => toggleSelect(doc.id)}
                  style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, border: 'none',
                    background: isSel ? 'var(--admin-teal)' : 'var(--admin-surface2)', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  {isSel ? <CheckSquare size={13}/> : <Square size={13} style={{ color: 'var(--admin-faint)' }}/>}
                </button>
                {/* Icône type */}
                <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: cfg.bg, color: cfg.color }}>
                  {cfg.icon}
                </div>
                {/* Infos */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 7,
                      background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                    {doc.numero && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-gold)',
                        fontFamily: 'var(--admin-font-display)', letterSpacing: '.04em' }}>{doc.numero}</span>
                    )}
                    <span style={{ fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 7,
                      background: 'var(--admin-surface2)', color: 'var(--admin-muted)' }}>{doc.statut}</span>
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 1 }}>
                    {doc.client_nom || '—'}
                  </p>
                  <p style={{ fontSize: 11.5, color: 'var(--admin-faint)' }}>{formatDate(doc.date)}</p>
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                  {/* Aperçu inline */}
                  {(doc.url_view || doc.signature_data) && (
                    <button onClick={() => setPreview(doc)} title="Aperçu inline"
                      style={{ width: 32, height: 32, borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: 'var(--admin-surface2)', color: 'var(--admin-muted)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ZoomIn size={13}/>
                    </button>
                  )}
                  {/* Ouvrir dans nouvel onglet */}
                  {doc.url_view && (
                    <a href={`${BACKEND}${doc.url_view}`} target="_blank" rel="noreferrer" title="Ouvrir"
                      style={{ width: 32, height: 32, borderRadius: 9,
                        background: 'var(--admin-blue-dim)', color: 'var(--admin-blue)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                      <ExternalLink size={13}/>
                    </a>
                  )}
                  {/* Renvoyer */}
                  {doc.can_resend && (
                    <button onClick={() => renvoyer(doc)} disabled={sending[doc.id]} title="Renvoyer par email"
                      style={{ width: 32, height: 32, borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: sending[doc.id] ? .6 : 1 }}>
                      <Send size={13}/>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Aperçu inline modal */}
      {preview && <InlinePreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
